import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useTranslation } from 'react-i18next';
import { ConnectedDatePickerContent } from './Content';
import { CMS, Config, DateUtils } from '../../utils';
import { Dropdown } from '../Dropdown/Dropdown';
import { ErrorUtils } from '../../utils/ErrorUtils';
import { CalendarType, CalendarTypeProp, FieldType, ProductManagerErrorProp, DateType, DateTypeProp } from '../../models';
import { CalendarContext } from '../../widgets/FlightsManager/CalendarContext';

export const DatePicker = ({
    dates,
    isDatesRangeShown,
    maxDate,
    minDate,
    onChangeFocus,
    onSelect,
    open,
    placeholder,
    roundedCornersFirst,
    roundedCornersLast,
    validationError,
    value,
    type,
    dateType,
    DatePickerError,
}) => {
    const [formattedMaxDate, setFormattedMaxDate] = useState();
    const [formattedMinDate, setFormattedMinDate] = useState();
    const [formattedValueDate, setFormattedValueDate] = useState();
    const [formattedDates, setFormattedDates] = useState([]);
    const [displayMonthState, setDisplayMonthState] = useState(0);
    const { userSelection, setUserSelection } = useContext(CalendarContext);
    useEffect(() => {
        setFormattedMaxDate(DateUtils.stringToDate(maxDate));
        setFormattedMinDate(DateUtils.stringToDate(minDate));
        setFormattedValueDate(DateUtils.stringToDate(value));
        setFormattedDates(dates.map((date) => DateUtils.stringToDate(date)));
    }, [dates, maxDate, minDate, value]);

    const checkMonthToRender = useCallback(() => {
        const departureMonthDifference = formattedDates[0] ? DateUtils.getDifferenceInMonths(DateUtils.getToday(), formattedDates[0]) : -1;
        const arrivalMonthDifference = formattedDates[1] ? DateUtils.getDifferenceInMonths(DateUtils.getToday(), formattedDates[1]) : -1;
        let displayMonth = departureMonthDifference >= 2 || arrivalMonthDifference >= 2 ?
            DateUtils.getFirstDayOfMonth(formattedValueDate || formattedMinDate || DateUtils.getToday()) :
            DateUtils.getFirstDayOfMonth(DateUtils.getToday());
        if (userSelection) {
            if (DateUtils.getDifferenceInMonths(displayMonth, userSelection) <= -1 && displayMonth !== formattedDates[1]) {
                displayMonth = userSelection;
            }
        }
        setDisplayMonthState(displayMonth);
    }, [formattedDates, formattedMinDate, formattedValueDate, userSelection]);

    useEffect(() => {
        checkMonthToRender();
    }, [userSelection, formattedDates, checkMonthToRender]);

    const { t } = useTranslation();
    const node = React.useRef(null);
    const hasError = !!validationError;
    const FLEXIBLE_DAYS = Config.get('daysWithFares');

    const dateFormat = t('calendar.dateFormatOneLiner');
    const formattedDate = formattedValueDate ? DateUtils.formatDate(formattedValueDate, dateFormat) : '';
    const classNames = className({
        'active': open,
        'invalid': hasError && !open,
        'odf-datepicker': true,
    });
    const inputClassNames = className({
        'focus odf-input-inline-focus': open,
        'odf-z-index-2': hasError && !open,
    });
    const inputFieldClassNames = className({
        'odf-input-m odf-text-lg odf-input odf-input-inline odf-clickable odf-text-ellipsis': true,
        'odf-input-inline-first': roundedCornersFirst,
        'odf-input-inline-last': roundedCornersLast,
    });

    const toggle = (toState = null) => {
        const value = typeof toState === 'boolean' ? toState : !open;
        onChangeFocus(FieldType.DATE, value);
    };

    const handleFocus = (e) => {
        toggle();
        checkMonthToRender();
        e.target.blur();
    };

    const onSelectWrapper = (date, moveFocus) => {
        if (moveFocus) {
            toggle(false);
        }
        onSelect(date);
    };
    const onCloseWrapper = () => {
        toggle(false);
    };
    return (
        <div className={classNames} ref={node}>
            <div className={inputClassNames}>
                <input
                    type="text"
                    className={inputFieldClassNames}
                    value={formattedDate}
                    readOnly
                    placeholder={placeholder}
                    onFocus={handleFocus}
                />
            </div>
            <Dropdown open={open} onClose={() => toggle(false)} parentRef={node}>
                <ConnectedDatePickerContent
                    dates={formattedDates}
                    displayMonth={displayMonthState}
                    isDatesRangeShown={isDatesRangeShown}
                    minDate={formattedMinDate}
                    maxDate={formattedMaxDate}
                    onSelect={onSelectWrapper}
                    onClose={onCloseWrapper}
                    value={formattedValueDate}
                    userSelection={userSelection}
                    userSelectionCallback={setUserSelection}
                    flexibleDays={FLEXIBLE_DAYS}
                    type={type}
                    dateType={dateType}
                />
            </Dropdown>
            {hasError && DatePickerError ? (
                <DatePickerError validationErrorKey={validationError} />
            ) : (
                <span className="odf-label odf-text-sm odf-space-outer-xs">
                    <CMS
                        id={ErrorUtils.getDatesErrorKey(validationError)}
                        values={ErrorUtils.getDatesErrorKeyArgs(validationError)}
                    />
                </span>
            )}
        </div>
    );
};

DatePicker.propTypes = {
    dates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
    isDatesRangeShown: PropTypes.bool.isRequired,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    onSelect: PropTypes.func,
    placeholder: PropTypes.string,
    value: PropTypes.instanceOf(Date),
    validationError: ProductManagerErrorProp,
    open: PropTypes.bool,
    onChangeFocus: PropTypes.func.isRequired,
    roundedCornersFirst: PropTypes.bool,
    roundedCornersLast: PropTypes.bool,
    type: CalendarTypeProp,
    dateType: DateTypeProp,
    DatePickerError: PropTypes.func,
};

DatePicker.defaultProps = {
    dates: [],
    onSelect: () => {},
    minDate: DateUtils.getToday(),
    maxDate: DateUtils.getLastValidDay(),
    value: null,
    open: false,
    roundedCornersFirst: false,
    roundedCornersLast: false,
    validationError: null,
    placeholder: '',
    type: CalendarType.DEPARTURE,
    dateType: DateType.FROM,
};
